import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin } from "lucide-react";
import { BRAND } from "@/data/portfolio";
import { useSite } from "@/contexts/SiteContext";
import { fileUrl } from "@/lib/api";

const NAV = [
  { to: "/weddings", label: "Weddings" },
  { to: "/films", label: "Films" },
  { to: "/pre-wedding", label: "Pre-Wedding" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  const { settings } = useSite() || {};
  const logoUrl = settings?.logoUrl;

  return (
    <footer data-testid="site-footer" data-gsap="reveal" className="bg-[#070707] border-t border-[rgba(184,145,74,.18)]">
      <div className="max-w-[1380px] mx-auto px-6 md:px-12">

        {/* Top row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 py-16 md:py-24">

          {/* Brand */}
          <div className="md:col-span-5">
            {logoUrl ? (
              <img src={fileUrl(logoUrl)} alt="Mangalfera Films"
                className="h-16 md:h-20 w-auto object-contain" />
            ) : (
              <div className="font-display text-[2.2rem] text-[#B8914A]">Mangalfera Films</div>
            )}
            <p className="mt-5 text-sm leading-[1.85] text-[rgba(245,239,228,.6)] max-w-sm">
              Cinematic wedding photography and films for couples across Ahmedabad,
              Gujarat, India, and destination weddings — crafted with emotion and elegance.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 border border-[rgba(184,145,74,.3)] flex items-center justify-center hover:border-[#B8914A] hover:text-[#B8914A] transition-colors">
                <Instagram size={15} />
              </a>
            </div>
          </div>

          {/* Nav */}
          <div className="md:col-span-3">
            <div className="eyebrow text-[#B8914A] mb-6 text-[0.6rem]">Navigate</div>
            <ul className="flex flex-col gap-3">
              {NAV.map(n => (
                <li key={n.to}>
                  <Link to={n.to}
                    className="text-[0.83rem] text-[rgba(245,239,228,.75)] hover:text-[#B8914A] transition-colors"
                    data-testid={`footer-link-${n.label.toLowerCase()}`}>
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="eyebrow text-[#B8914A] mb-6 text-[0.6rem]">Get In Touch</div>
            <ul className="flex flex-col gap-4 text-[0.83rem]">
              <li className="flex items-start gap-3">
                <Phone size={14} className="mt-[3px] text-[#B8914A] flex-shrink-0" />
                <a href={`tel:+91${BRAND.phone}`}
                  className="text-[rgba(245,239,228,.8)] hover:text-[#B8914A] transition-colors"
                  data-testid="footer-phone">+91 {BRAND.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={14} className="mt-[3px] text-[#B8914A] flex-shrink-0" />
                <a href={`mailto:${BRAND.email}`}
                  className="text-[rgba(245,239,228,.8)] hover:text-[#B8914A] transition-colors"
                  data-testid="footer-email">{BRAND.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={14} className="mt-[3px] text-[#B8914A] flex-shrink-0" />
                <span className="text-[rgba(245,239,228,.7)]">{BRAND.serviceArea}</span>
              </li>
              <li className="flex items-start gap-3">
                <Instagram size={14} className="mt-[3px] text-[#B8914A] flex-shrink-0" />
                <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer"
                  className="text-[rgba(245,239,228,.8)] hover:text-[#B8914A] transition-colors"
                  data-testid="footer-instagram">{BRAND.instagramHandle}</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-5 border-t border-[rgba(184,145,74,.12)] flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <span className="eyebrow text-[0.58rem] text-[rgba(245,239,228,.35)]">
            © {new Date().getFullYear()} Mangalfera Films · All rights reserved
          </span>
          <span className="eyebrow text-[0.58rem] text-[rgba(184,145,74,.6)]">
            Cinematic Wedding Stories, Beautifully Told
          </span>
        </div>
      </div>
    </footer>
  );
}
